import { useSyncExternalStore } from "react";
import {
  getAccessToken,
  getSelectedContext,
  getStaffSession,
  getStaffToken,
  subscribeSessionChange,
} from "@/lib/api";

/** Who this tab's notification socket is for. */
export interface StaffConnection {
  outletId: string;
  profileId: string;
}

/**
 * This tab's staff session as one string, "" when nobody is signed in at an
 * outlet. It changes on sign-in, on picking an outlet or a person (PIN), on
 * Switch Outlet/Staff, and on sign-out; a token refresh doesn't change it (the
 * socket takes a fresh token on every connect).
 */
export function staffConnectionKey(): string {
  if (!getAccessToken()) return "";
  const context = getSelectedContext();
  const session = getStaffSession();
  const outletId = context?.outlet?.id || session?.profile?.outlet_id || "";
  // `profile.id` is what the PIN verify sends; `profile_id` only older stored sessions.
  const profileId = session?.profile?.id || session?.profile?.profile_id || context?.staffProfile?.id || "";
  if (!outletId || !profileId) return "";
  // The staff token tells two sign-ins of the same person apart (a new session).
  return JSON.stringify([outletId, profileId, getStaffToken() ?? ""]);
}

/** The outlet and profile in a staffConnectionKey(), or null for "". */
export function parseStaffConnectionKey(key: string): StaffConnection | null {
  if (!key) return null;
  try {
    const [outletId, profileId] = JSON.parse(key) as [string, string, string];
    return outletId && profileId ? { outletId, profileId } : null;
  } catch {
    return null;
  }
}

/** staffConnectionKey(), re-read whenever the session changes. */
export function useStaffConnectionKey(): string {
  return useSyncExternalStore(subscribeSessionChange, staffConnectionKey, staffConnectionKey);
}
